// ============================================
// Firebase Client Configuration
// Uses existing asili-guide Firebase project
// ============================================

import { initializeApp, getApps } from 'firebase/app';
import type { FirebaseApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import type { Firestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import type { FirebaseStorage } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import type { Auth } from 'firebase/auth';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';
import type { Functions } from 'firebase/functions';
import { getAnalytics, isSupported as analyticsSupported } from 'firebase/analytics';

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

// Validate config
function validateConfig(): void {
  const requiredKeys = [
    'apiKey',
    'authDomain',
    'projectId',
    'storageBucket',
    'messagingSenderId',
    'appId',
  ];

  const missing = requiredKeys.filter(
    key => !firebaseConfig[key as keyof typeof firebaseConfig]
  );

  if (missing.length > 0) {
    console.error('Missing Firebase config keys:', missing);
    throw new Error(`Missing Firebase configuration: ${missing.join(', ')}`);
  }
}

// Singleton instances
let app: FirebaseApp | null = null;
let db: Firestore | null = null;
let storage: FirebaseStorage | null = null;
let auth: Auth | null = null;
let functions: Functions | null = null;

// Initialize Firebase app
export function initializeFirebase(): FirebaseApp {
  if (app) return app;

  validateConfig();

  if (getApps().length === 0) {
    app = initializeApp(firebaseConfig);
  } else {
    app = getApps()[0];
  }

  // Google Analytics (GA4): auto-collects page_view, session, and visitor
  // counts. Guarded — never breaks the app if unsupported (SSR, blockers).
  if (firebaseConfig.measurementId) {
    const initializedApp = app;
    analyticsSupported()
      .then((ok) => { if (ok) getAnalytics(initializedApp); })
      .catch(() => { /* analytics is optional */ });
  }

  return app;
}

// Get Firestore instance
export function getDb(): Firestore {
  if (!db) {
    db = getFirestore(initializeFirebase());
  }
  return db;
}

// Get Storage instance
export function getFirebaseStorage(): FirebaseStorage {
  if (!storage) {
    storage = getStorage(initializeFirebase());
  }
  return storage;
}

// Get Auth instance
export function getFirebaseAuth(): Auth {
  if (!auth) {
    auth = getAuth(initializeFirebase());
  }
  return auth;
}

// Get Functions instance
export function getFirebaseFunctions(): Functions {
  if (!functions) {
    functions = getFunctions(initializeFirebase());

    // Connect to emulator in development
    if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
      connectFunctionsEmulator(functions, 'localhost', 5002);
    }
  }
  return functions;
}

// Re-export types
export type { Firestore, FirebaseStorage, Auth, Functions };
